import { z } from 'zod';
import { ChatSchema, ImageOptimizationSchema, CategorySchema, CollectionSchema, TemplateSchema, EventCreatorSchema } from './common.js';

/**
 * Events endpoint Zod schemas for Polymarket Gamma API
 */

export const ListEventsParamsSchema = z.object({
  limit: z.number().optional(),
  offset: z.number().optional(),
  order: z.string().optional(),
  ascending: z.boolean().optional(),
  id: z.array(z.number()).optional(),
  slug: z.array(z.string()).optional(),
  tag_id: z.number().optional(),
  exclude_tag_id: z.array(z.number()).optional(),
  related_tags: z.boolean().optional(),
  featured: z.boolean().optional(),
  cyom: z.boolean().optional(),
  include_chat: z.boolean().optional(),
  include_template: z.boolean().optional(),
  recurrence: z.string().optional(),
  closed: z.boolean().optional(),
  start_date_min: z.string().optional(),
  start_date_max: z.string().optional(),
  end_date_min: z.string().optional(),
  end_date_max: z.string().optional(),
});

export const GetEventByIdParamsSchema = z.object({
  id: z.number(),
  include_chat: z.boolean().optional(),
  include_template: z.boolean().optional(),
});

export const GetEventBySlugParamsSchema = z.object({
  slug: z.string(),
  include_chat: z.boolean().optional(),
  include_template: z.boolean().optional(),
});

export const GetEventTagsParamsSchema = z.object({
  id: z.number(),
});

// Forward declarations for circular dependencies
const MarketSchema = z.unknown();
const SeriesSchema = z.unknown();
const TagSchema = z.unknown();

export const EventSchema = z.object({
  id: z.string(),
  ticker: z.string().nullable().optional(),
  slug: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  subtitle: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  resolutionSource: z.string().nullable().optional(),
  startDate: z.string().nullable().optional(),
  creationDate: z.string().nullable().optional(),
  endDate: z.string().nullable().optional(),
  image: z.string().nullable().optional(),
  icon: z.string().nullable().optional(),
  active: z.boolean().nullable().optional(),
  closed: z.boolean().nullable().optional(),
  archived: z.boolean().nullable().optional(),
  new: z.boolean().nullable().optional(),
  featured: z.boolean().nullable().optional(),
  restricted: z.boolean().nullable().optional(),
  liquidity: z.number().nullable().optional(),
  volume: z.number().nullable().optional(),
  openInterest: z.number().nullable().optional(),
  sortBy: z.string().nullable().optional(),
  category: z.string().nullable().optional(),
  subcategory: z.string().nullable().optional(),
  isTemplate: z.boolean().nullable().optional(),
  templateVariables: z.string().nullable().optional(),
  published_at: z.string().nullable().optional(),
  createdBy: z.string().nullable().optional(),
  updatedBy: z.string().nullable().optional(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  commentsEnabled: z.boolean().nullable().optional(),
  competitive: z.number().nullable().optional(),
  volume24hr: z.number().nullable().optional(),
  volume1wk: z.number().nullable().optional(),
  volume1mo: z.number().nullable().optional(),
  volume1yr: z.number().nullable().optional(),
  featuredImage: z.string().nullable().optional(),
  disqusThread: z.string().nullable().optional(),
  parentEvent: z.string().nullable().optional(),
  enableOrderBook: z.boolean().nullable().optional(),
  liquidityAmm: z.number().nullable().optional(),
  liquidityClob: z.number().nullable().optional(),
  negRisk: z.boolean().nullable().optional(),
  negRiskMarketID: z.string().nullable().optional(),
  negRiskFeeBips: z.number().nullable().optional(),
  commentCount: z.number().nullable().optional(),
  imageOptimized: ImageOptimizationSchema.optional(),
  iconOptimized: ImageOptimizationSchema.optional(),
  featuredImageOptimized: ImageOptimizationSchema.optional(),
  subEvents: z.array(z.string()).nullable().optional(),
  markets: z.array(MarketSchema).optional(),
  series: z.array(SeriesSchema).optional(),
  categories: z.array(CategorySchema).optional(),
  collections: z.array(CollectionSchema).optional(),
  tags: z.array(TagSchema).optional(),
  cyom: z.boolean().nullable().optional(),
  closedTime: z.string().nullable().optional(),
  showAllOutcomes: z.boolean().nullable().optional(),
  showMarketImages: z.boolean().nullable().optional(),
  automaticallyResolved: z.boolean().nullable().optional(),
  enableNegRisk: z.boolean().nullable().optional(),
  automaticallyActive: z.boolean().nullable().optional(),
  eventDate: z.string().nullable().optional(),
  startTime: z.string().nullable().optional(),
  eventWeek: z.number().nullable().optional(),
  seriesSlug: z.string().nullable().optional(),
  score: z.string().nullable().optional(),
  elapsed: z.string().nullable().optional(),
  period: z.string().nullable().optional(),
  live: z.boolean().nullable().optional(),
  ended: z.boolean().nullable().optional(),
  finishedTimestamp: z.string().nullable().optional(),
  gmpChartMode: z.string().nullable().optional(),
  eventCreators: z.array(EventCreatorSchema).optional(),
  tweetCount: z.number().nullable().optional(),
  chats: z.array(ChatSchema).optional(),
  featuredOrder: z.number().nullable().optional(),
  estimateValue: z.boolean().nullable().optional(),
  cantEstimate: z.boolean().nullable().optional(),
  estimatedValue: z.string().nullable().optional(),
  templates: z.array(TemplateSchema).optional(),
  spreadsMainLine: z.number().nullable().optional(),
  totalsMainLine: z.number().nullable().optional(),
  carouselMap: z.string().nullable().optional(),
  pendingDeployment: z.boolean().nullable().optional(),
  deploying: z.boolean().nullable().optional(),
  deployingTimestamp: z.string().nullable().optional(),
  scheduledDeploymentTimestamp: z.string().nullable().optional(),
  gameStatus: z.string().nullable().optional(),
});
